import { useMemo } from 'react';
import { ColumnFiltersState, Table } from '@tanstack/react-table';
import { TableNullStateType } from '../components/TableNullState/TableNullState';

interface UseTableNullStateProps {
  columnFilters: ColumnFiltersState;
  table: Table<any>;
  filterIds: string[];
  defaultNullStateType: TableNullStateType;
  globalFilter?: string;
}

/**
 * Hook to determine the appropriate null state type for a table based on applied filters.
 *
 * @param columnFilters - The current column filters state
 * @param table - The react-table instance
 * @param filterIds - Array of column IDs to check for applied filters
 * @param defaultNullStateType - The default null state type when no filters are applied
 * @param globalFilter - The current global filter value (search field)
 * @returns The appropriate TableNullStateType
 */
export const useTableNullState = ({
  columnFilters,
  table,
  filterIds,
  defaultNullStateType,
  globalFilter,
}: UseTableNullStateProps): TableNullStateType => {
  return useMemo(() => {
    const filterValues = filterIds.map((id) => {
      const filter = columnFilters.find((f) => f.id === id);
      return filter?.value as string[] | undefined;
    });
    const hasFilterApplied = filterValues.some((value) => Array.isArray(value) && value.length > 0);

    // Check if there's a global filter (search field) applied
    const hasGlobalFilter = globalFilter && globalFilter !== '';

    if (hasFilterApplied || hasGlobalFilter) {
      return TableNullStateType.NO_RESULTS_FROM_FILTERS;
    }
    return defaultNullStateType;
  }, [columnFilters, table.getRowModel().rows?.length, filterIds, defaultNullStateType, globalFilter]);
};
